<script setup>
import { ref, onMounted } from "vue";
import { useGetters, useState } from "@/store/vuex-composition-mappers";
import ComponentThumbnail from "./ComponentThumbnail.vue";
import { setupDragStart } from "@/apps/page/project/sidebar/sections/dragOverlay";

const props = defineProps({
    blocks: {
        type: Array,
        required: true,
    },
    currentSection: {
        type: [Object, null],
        required: false,
    },
});

const { pageColors, pageColorsStyles } = useGetters("page/theme", [
    "pageColors",
    "pageColorsStyles",
]);

const { page } = useState("page", ["page"]);
const emit = defineEmits(["dragStart"]);

const showPreviews = ref(false);
const blockRefs = ref([]);

onMounted(() => {
    setTimeout(() => {
        // BF: Wait for the component to load before showing the previews to not block the UI
        showPreviews.value = true;
    });
});

const isBlockVisible = (block) => {
    if (!props.currentSection) return true;
    return block.type === props.currentSection.type;
};

async function handleDragStart(event, block, index) {
    const blockElement = blockRefs.value[index];

    await setupDragStart(
        event,
        {
            type: "block",
            data: block,
        },
        blockElement,
        {
            fallbackText: block.name,
            paddingX: 16,
            paddingY: 20,
        }
    );

    emit("dragStart");
}

function setBlockRef(index, el) {
    if (el && el.$el) {
        blockRefs.value[index] = el.$el;
    } else if (el && el instanceof HTMLElement) {
        blockRefs.value[index] = el;
    }
}
</script>

<template>
    <div class="p-6 flex flex-col gap-10">
        <div
            v-for="(block, index) in blocks"
            :key="block.type + index"
            class="flex items-center flex-col gap-4 ct-ring-1 hover:ct-ring-brand cursor-move transition-all duration-200"
            :class="{ hidden: !isBlockVisible(block) }"
            draggable="true"
            @dragstart="(e) => handleDragStart(e, block, index)"
        >
            <template v-if="showPreviews">
                <ComponentThumbnail
                    v-if="block.component"
                    :ref="(el) => setBlockRef(index, el)"
                    :block="block"
                    :page-colors="pageColors"
                    :page-colors-styles="pageColorsStyles"
                    :scroll-animation-type="page.settings?.scrollAnimationType"
                />
            </template>
            <template v-else>
                <div class="w-full h-32 ct-bg-3 animate-pulse rounded"></div>
            </template>

            <div class="text-sm w-full flex justify-center" :title="block.name">
                <p class="font-medium ct-text-1 text-center">{{ block.name }}</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}
</style>
